import { Button, StatusMessage } from '@krp/ui-kit'
import type { QueryView } from '@krp/client-runtime'

import { metaOf } from './meta'

/**
 * Заголовок недоступности. Служба назвала причину кодом: `source-unavailable`
 * — не ответила сама 1С, `source-unauthorized` — 1С не приняла учётные данные
 * подключения (RP-LSB1). Без кода — недоступна служба или шлюз, и сказать
 * «1С» было бы неправдой.
 */
function unavailableTitle(code: string | undefined): string {
  if (code === 'source-unavailable') return '1С недоступна'
  if (code === 'source-unauthorized') return '1С не приняла учётные данные подключения'
  return '1С или служба недоступна'
}

/**
 * Не-«готовые» состояния чтения одинаково во всех окнах пакета: загрузка,
 * пусто, нет входа, нет доступа, недоступно, ошибка. Текст службы 1С —
 * дословно (ADR-8), код и корреляция — для отчёта.
 */
export function OneCQueryState({
  query,
  emptyText,
  onRetry,
}: {
  query: QueryView
  emptyText: string
  onRetry: () => void
}) {
  const meta = query.error !== null ? metaOf(query.error.code, query.error.correlationId) : undefined
  const retry = (
    <Button variant="secondary" onClick={onRetry}>
      Повторить
    </Button>
  )
  switch (query.status) {
    case 'idle':
      return null
    case 'loading':
      return <StatusMessage kind="loading" title="Загрузка…" />
    case 'empty':
      return <StatusMessage kind="empty" title={emptyText} />
    case 'unauthorized':
      return <StatusMessage kind="unauthorized" title="Сессия завершена" text="Войдите снова, чтобы продолжить." />
    case 'forbidden':
      return (
        <StatusMessage kind="forbidden" title="Нет доступа" text={query.error?.message} {...(meta ? { meta } : {})} />
      )
    case 'unavailable':
      return (
        <StatusMessage
          kind="unavailable"
          title={unavailableTitle(query.error?.code)}
          text={query.error?.message}
          {...(meta ? { meta } : {})}
          action={retry}
        />
      )
    case 'error':
      return (
        <StatusMessage
          kind="error"
          title="Ошибка"
          text={query.error?.message}
          {...(meta ? { meta } : {})}
          action={retry}
        />
      )
    case 'ready':
      return null
  }
}
